import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CorkBoardItem {
  id: string;
  type: 'note' | 'photo';
  content?: string;
  photo_url?: string;
  caption?: string;
  position_x: number;
  position_y: number;
  z_index: number;
  rotation: number;
  is_locked?: boolean;
  color?: string;
  pattern?: string;
  created_at?: string;
}

// 임시 아이템 인터페이스 (로컬 전용)
interface TempItem extends CorkBoardItem {
  isTemp: boolean;
}

export default function HomePage() {
  const [items, setItems] = useState<CorkBoardItem[]>([]);
  const [tempItems, setTempItems] = useState<TempItem[]>([]); // 임시 아이템 (본인만 보임)
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [maxZIndex, setMaxZIndex] = useState(0);
  const [editingNote, setEditingNote] = useState<{ id: string; content: string; color: string; pattern: string } | null>(null);
  const [placingNoteId, setPlacingNoteId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState<{ url: string; caption: string } | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPassword, setNewGroupPassword] = useState('');
  const [noPassword, setNoPassword] = useState(false);

  // 형광색 포스트잇 색상 팔레트
  const noteColors = [
    { name: '노란색', color: '#fef08a', border: '#fde047' },
    { name: '핑크색', color: '#fecdd3', border: '#fda4af' },
    { name: '주황색', color: '#fed7aa', border: '#fdba74' },
    { name: '초록색', color: '#bbf7d0', border: '#86efac' },
    { name: '하늘색', color: '#bfdbfe', border: '#93c5fd' },
    { name: '보라색', color: '#e9d5ff', border: '#d8b4fe' }
  ];

  // 무늬 패턴
  const notePatterns = [
    { name: '무늬 없음', value: 'none', icon: 'ri-close-line' },
    { name: '하트', value: 'heart', icon: 'ri-heart-line' },
    { name: '별', value: 'star', icon: 'ri-star-line' },
    { name: '꽃', value: 'flower', icon: 'ri-flower-line' },
    { name: '도트', value: 'dot', icon: 'ri-checkbox-blank-circle-line' }
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadItems();
    
    const channel = supabase
      .channel('cork_board_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cork_board_items' },
        () => {
          loadItems();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadItems = async () => {
    const { data, error } = await supabase
      .from('cork_board_items')
      .select('*')
      .is('group_name', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading items:', error);
      return;
    }

    if (data) {
      setItems(data);
      const maxZ = Math.max(...data.map(item => item.z_index), 0);
      setMaxZIndex(maxZ);
    }
  };

  // 모든 아이템 (공유된 아이템 + 임시 아이템)
  const allItems = [...items, ...tempItems];

  const handleMouseDown = (e: React.MouseEvent, itemId: string) => {
    if (isMobile) return;
    
    const item = allItems.find(i => i.id === itemId);
    if (!item) return;

    // 배치 중인 항목(placingNoteId)이거나 is_locked가 false인 경우 드래그 가능
    // is_locked가 true인 경우에만 드래그 방지
    if (item.is_locked && placingNoteId !== itemId) return;

    e.stopPropagation();
    setDraggingId(itemId);
    setDragOffset({
      x: e.clientX - item.position_x,
      y: e.clientY - item.position_y
    });

    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    
    updateItemZIndex(itemId, newZIndex);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isMobile || !draggingId) return;
    
    const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - 250));
    const newY = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - 250));

    // 임시 아이템인지 확인
    const isTempItem = tempItems.some(item => item.id === draggingId);
    
    if (isTempItem) {
      setTempItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    } else {
      setItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    }
  };

  const handleMouseUp = async () => {
    if (isMobile || !draggingId) return;
    
    const item = allItems.find(i => i.id === draggingId);
    if (item) {
      // 임시 아이템이 아닌 경우에만 DB 업데이트
      const isTempItem = tempItems.some(t => t.id === draggingId);
      if (!isTempItem) {
        await updateItemPosition(draggingId, item.position_x, item.position_y);
      }
    }
    setDraggingId(null);
  };

  const handleTouchStart = (e: React.TouchEvent, itemId: string) => {
    if (isMobile) return;
    
    const item = allItems.find(i => i.id === itemId);
    if (!item) return;

    // 배치 중인 항목(placingNoteId)이거나 is_locked가 false인 경우 드래그 가능
    // is_locked가 true인 경우에만 드래그 방지
    if (item.is_locked && placingNoteId !== itemId) return;

    const touch = e.touches[0];
    setDraggingId(itemId);
    setDragOffset({
      x: touch.clientX - item.position_x,
      y: touch.clientY - item.position_y
    });

    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    updateItemZIndex(itemId, newZIndex);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isMobile || !draggingId || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const newX = Math.max(0, Math.min(touch.clientX - dragOffset.x, window.innerWidth - 250));
    const newY = Math.max(0, Math.min(touch.clientY - dragOffset.y, window.innerHeight - 250));

    // 임시 아이템인지 확인
    const isTempItem = tempItems.some(item => item.id === draggingId);
    
    if (isTempItem) {
      setTempItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    } else {
      setItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    }
  };

  const handleTouchEnd = async () => {
    if (isMobile || !draggingId) return;
    
    const item = allItems.find(i => i.id === draggingId);
    if (item) {
      // 임시 아이템이 아닌 경우에만 DB 업데이트
      const isTempItem = tempItems.some(t => t.id === draggingId);
      if (!isTempItem) {
        await updateItemPosition(draggingId, item.position_x, item.position_y);
      }
    }
    setDraggingId(null);
  };

  const updateItemZIndex = async (itemId: string, zIndex: number) => {
    // 임시 아이템인 경우 DB 업데이트 안 함
    const isTempItem = tempItems.some(item => item.id === itemId);
    if (isTempItem) {
      setTempItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, z_index: zIndex } : item
        )
      );
      return;
    }

    await supabase
      .from('cork_board_items')
      .update({ z_index: zIndex })
      .eq('id', itemId);
  };

  const updateItemPosition = async (itemId: string, x: number, y: number) => {
    await supabase
      .from('cork_board_items')
      .update({
        position_x: x,
        position_y: y
      })
      .eq('id', itemId);
  };

  const confirmPlacement = async () => {
    if (!placingNoteId) return;

    const tempItem = tempItems.find(i => i.id === placingNoteId);
    if (!tempItem) return;

    // 임시 아이템을 DB에 저장 (다른 사용자와 공유)
    // id 필드를 제외하고 저장 (Supabase가 자동으로 UUID 생성)
    const { isTemp, id, ...itemData } = tempItem;
    const { data, error } = await supabase
      .from('cork_board_items')
      .insert([{
        ...itemData,
        is_locked: true
      }])
      .select()
      .single();

    if (!error && data) {
      // 임시 아이템 제거
      setTempItems(prev => prev.filter(i => i.id !== placingNoteId));
      // 공유 아이템에 추가
      setItems(prev => [data as CorkBoardItem, ...prev]);
      setPlacingNoteId(null);
    } else {
      console.error('Error saving item:', error);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const addNote = () => {
    setShowAddMenu(false);
    setEditingNote({ id: 'new', content: '', color: '#fef08a', pattern: 'none' });
  };

  const compressImage = (file: File, maxSizeMB: number = 2): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('이미지 압축에 실패했습니다.'));
                  return;
                }

                const compressedSize = blob.size / 1024 / 1024;
                if (compressedSize > maxSizeMB && quality > 0.1) {
                  quality -= 0.1;
                  tryCompress();
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                }
              },
              'image/jpeg',
              quality
            );
          };

          tryCompress();
        };
        img.onerror = () => reject(new Error('이미지 로드에 실패했습니다.'));
      };
      reader.onerror = () => reject(new Error('파일 읽기에 실패했습니다.'));
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const maxFileSize = 50 * 1024 * 1024;
    if (file.size > maxFileSize) {
      alert('파일 크기가 너무 큽니다. 50MB 이하의 이미지를 선택해주세요.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      let processedFile = file;
      const fileSizeMB = file.size / 1024 / 1024;
      
      if (fileSizeMB > 2) {
        try {
          processedFile = await compressImage(file, 2);
        } catch (compressError) {
          console.error('Image compression error:', compressError);
          alert('이미지 처리 중 오류가 발생했습니다. 다른 이미지를 선택해주세요.');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }

      const fileExt = 'jpg';
      const fileName = `${Date.now()}_${Math.random()}.${fileExt}`;
      const filePath = `photos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('cork-board-photos')
        .upload(filePath, processedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Error uploading photo:', uploadError);
        alert('사진 업로드에 실패했습니다. 다시 시도해주세요.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('cork-board-photos')
        .getPublicUrl(filePath);

      setUploadingPhoto({ url: publicUrl, caption: '' });
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('예상치 못한 오류가 발생했습니다. 다시 시도해주세요.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setShowAddMenu(false);
    
    // 파일 입력 필드 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhotoSave = async () => {
    if (!uploadingPhoto) return;

    const newZIndex = maxZIndex + 1;
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    
    // 모바일에서는 랜덤 위치 생성
    let centerX, centerY, rotation;
    if (isMobile) {
      // 화면 크기 고려하여 랜덤 위치 생성 (여백 고려)
      const maxX = window.innerWidth - 250;
      const maxY = window.innerHeight - 300;
      centerX = Math.random() * Math.max(0, maxX);
      centerY = Math.random() * Math.max(0, maxY);
      rotation = Math.random() * 6 - 3; // -3도 ~ +3도
      
      // 모바일에서는 즉시 DB에 저장 (위치 확정 불필요)
      const newPhoto = {
        type: 'photo',
        photo_url: uploadingPhoto.url,
        caption: uploadingPhoto.caption,
        position_x: centerX,
        position_y: centerY,
        z_index: newZIndex,
        rotation: rotation,
        is_locked: true
      };

      const { data, error } = await supabase
        .from('cork_board_items')
        .insert([newPhoto])
        .select()
        .single();

      if (!error && data) {
        const newItem = data as CorkBoardItem;
        setItems(prev => [newItem, ...prev]);
        setMaxZIndex(newZIndex);
        setUploadingPhoto(null);
      } else {
        console.error('Error saving photo:', error);
        alert('사진 저장에 실패했습니다. 다시 시도해주세요.');
      }
    } else {
      // PC에서는 중앙 배치 후 임시 아이템으로 추가
      centerX = Math.max(0, Math.min(window.innerWidth / 2 - 110, window.innerWidth - 220));
      centerY = Math.max(0, Math.min(window.innerHeight / 2 - 130, window.innerHeight - 260));
      rotation = Math.random() * 6 - 3;
      
      const tempPhoto: TempItem = {
        id: tempId,
        type: 'photo',
        photo_url: uploadingPhoto.url,
        caption: uploadingPhoto.caption,
        position_x: centerX,
        position_y: centerY,
        z_index: newZIndex,
        rotation: rotation,
        is_locked: false,
        isTemp: true
      };

      setTempItems(prev => [tempPhoto, ...prev]);
      setMaxZIndex(newZIndex);
      setPlacingNoteId(tempId);
      setUploadingPhoto(null);
    }
  };

  const handlePhotoCancel = () => {
    setUploadingPhoto(null);
    // 파일 입력 필드 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (itemId: string) => {
    const item = allItems.find(i => i.id === itemId);
    
    // 모바일에서는 확인 후 삭제
    if (isMobile) {
      const confirmDelete = window.confirm('정말 삭제하시겠습니까?');
      if (!confirmDelete) return;
    }
    
    // 임시 아이템인 경우 로컬에서만 제거
    const isTempItem = tempItems.some(t => t.id === itemId);
    if (isTempItem) {
      setTempItems(prev => prev.filter(i => i.id !== itemId));
      if (placingNoteId === itemId) {
        setPlacingNoteId(null);
      }
      return;
    }
    
    // 등록 전 아이템(is_locked가 false)은 즉시 삭제
    if (item && !item.is_locked) {
      setItems(prev => prev.filter(i => i.id !== itemId));
      
      if (placingNoteId === itemId) {
        setPlacingNoteId(null);
      }
      
      await supabase
        .from('cork_board_items')
        .delete()
        .eq('id', itemId);
      
      return;
    }
    
    // 등록된 아이템은 기존 로직 유지
    await supabase
      .from('cork_board_items')
      .delete()
      .eq('id', itemId);
  };

  const handleNoteSave = async () => {
    if (!editingNote) return;

    const newZIndex = maxZIndex + 1;
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    
    // 모바일에서는 랜덤 위치 생성
    let centerX, centerY, rotation;
    if (isMobile) {
      // 화면 크기 고려하여 랜덤 위치 생성 (여백 고려)
      const maxX = window.innerWidth - 200;
      const maxY = window.innerHeight - 250;
      centerX = Math.random() * Math.max(0, maxX);
      centerY = Math.random() * Math.max(0, maxY);
      rotation = Math.random() * 6 - 3; // -3도 ~ +3도
      
      // 모바일에서는 즉시 DB에 저장 (위치 확정 불필요)
      const newNote = {
        type: 'note',
        content: editingNote.content,
        color: editingNote.color,
        pattern: editingNote.pattern,
        position_x: centerX,
        position_y: centerY,
        z_index: newZIndex,
        rotation: rotation,
        is_locked: true
      };

      const { data, error } = await supabase
        .from('cork_board_items')
        .insert([newNote])
        .select()
        .single();

      if (!error && data) {
        const newItem = data as CorkBoardItem;
        setItems(prev => [newItem, ...prev]);
        setMaxZIndex(newZIndex);
        setEditingNote(null);
      } else {
        console.error('Error saving note:', error);
      }
    } else {
      // PC에서는 중앙 배치 후 임시 아이템으로 추가
      centerX = Math.max(0, Math.min(window.innerWidth / 2 - 96, window.innerWidth - 192));
      centerY = Math.max(0, Math.min(window.innerHeight / 2 - 96, window.innerHeight - 192));
      rotation = Math.random() * 6 - 3;
      
      const tempNote: TempItem = {
        id: tempId,
        type: 'note',
        content: editingNote.content,
        color: editingNote.color,
        pattern: editingNote.pattern,
        position_x: centerX,
        position_y: centerY,
        z_index: newZIndex,
        rotation: rotation,
        is_locked: false,
        isTemp: true
      };

      setTempItems(prev => [tempNote, ...prev]);
      setMaxZIndex(newZIndex);
      setPlacingNoteId(tempId);
      setEditingNote(null);
    }
  };

  const handleNoteCancel = () => {
    setEditingNote(null);
  };

  // 패턴 SVG 생성 함수
  const getPatternSVG = (pattern: string, color: string) => {
    const patternColor = color === '#fef08a' ? '#fde047' : 
                        color === '#fecdd3' ? '#fda4af' :
                        color === '#fed7aa' ? '#fdba74' :
                        color === '#bbf7d0' ? '#86efac' :
                        color === '#bfdbfe' ? '#93c5fd' :
                        '#d8b4fe';

    switch (pattern) {
      case 'heart':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12c-1.5-3-5-4-7-2s-2 5 0 7l7 7 7-7c2-2 2-5 0-7s-5.5-1-7 2z" 
                  fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      case 'star':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 10l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z" 
                  fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      case 'flower':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="15" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="25" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="20" cy="25" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="15" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="20" cy="20" r="2" fill="${patternColor}" opacity="0.5"/>
          </svg>
        `)}`;
      case 'dot':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      default:
        return 'none';
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert('그룹 이름을 입력해주세요.');
      return;
    }

    // 그룹 이름 유효성 검사 (영문, 숫자, 한글, 하이픈, 언더스코어만 허용)
    const validGroupName = /^[a-zA-Z0-9가-힣_-]+$/;
    if (!validGroupName.test(newGroupName.trim())) {
      alert('그룹 이름은 영문, 숫자, 한글, 하이픈(-), 언더스코어(_)만 사용할 수 있습니다.');
      return;
    }

    // 비밀번호 체크
    if (!noPassword && !newGroupPassword.trim()) {
      alert('비밀번호를 입력하거나 "비밀번호 없음"을 체크해주세요.');
      return;
    }

    // 중복 확인
    const { data: existingGroup } = await supabase
      .from('cork_board_items')
      .select('group_name')
      .eq('group_name', newGroupName.trim())
      .limit(1);

    if (existingGroup && existingGroup.length > 0) {
      alert('이미 존재하는 그룹 이름입니다.');
      return;
    }

    // 그룹 생성 (초기 더미 데이터 삽입)
    const { error } = await supabase
      .from('cork_board_items')
      .insert([{
        type: 'note',
        content: '환영합니다! 이 그룹의 첫 번째 메모입니다.',
        color: '#fef08a',
        pattern: 'none',
        position_x: 100,
        position_y: 100,
        z_index: 1,
        rotation: 2,
        is_locked: true,
        group_name: newGroupName.trim(),
        password: noPassword ? null : newGroupPassword.trim()
      }]);

    if (error) {
      console.error('Error creating group:', error);
      alert('그룹 생성에 실패했습니다.');
      return;
    }

    alert(`그룹 "${newGroupName.trim()}"이(가) 생성되었습니다!`);
    setNewGroupName('');
    setNewGroupPassword('');
    setNoPassword(false);
    setShowGroupCreate(false);
  };

  // PC 버전 렌더링
  if (!isMobile) {
    return (
      <div
        className="w-full h-screen overflow-hidden relative cursor-default select-none"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7ec64f7075a83e09c793ffae81f699dc.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          touchAction: 'none'
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Help Icon */}
        <button
          className="fixed top-4 right-4 md:top-8 md:right-8 w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-gray-50 rounded-full shadow-lg flex items-center justify-center text-amber-600 text-lg md:text-xl transition-all hover:scale-110 z-[9999] cursor-pointer"
          onClick={() => setShowHelpModal(true)}
        >
          <i className="ri-question-line" />
        </button>

        {/* Help Modal */}
        {showHelpModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 w-full max-w-[500px] max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <i className="ri-information-line text-amber-600" />
                  코르크보드 사용 가이드
                </h2>
                <button
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  onClick={() => setShowHelpModal(false)}
                >
                  <i className="ri-close-line text-2xl" />
                </button>
              </div>

              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-lg font-semibold text-amber-600 mb-2 flex items-center gap-2">
                    <i className="ri-sticky-note-line" />
                    메모 추가하기
                  </h3>
                  <p className="text-sm leading-relaxed">
                    오른쪽 하단의 <strong>+ 버튼</strong>을 클릭하고 "메모 추가"를 선택하세요. 
                    원하는 내용을 작성한 후 확인을 누르면 코르크보드에 메모가 추가됩니다.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-amber-600 mb-2 flex items-center gap-2">
                    <i className="ri-image-line" />
                    사진 추가하기
                  </h3>
                  <p className="text-sm leading-relaxed">
                    오른쪽 하단의 <strong>+ 버튼</strong>을 클릭하고 "사진 추가"를 선택하세요. 
                    이미지 파일을 선택하고 사진 설명을 입력하면 폴라로이드 스타일로 추가됩니다.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-amber-600 mb-2 flex items-center gap-2">
                    <i className="ri-drag-move-line" />
                    위치 조정하기
                  </h3>
                  <p className="text-sm leading-relaxed">
                    메모나 사진을 추가하면 <strong>원하는 위치로 드래그</strong>할 수 있습니다. 
                    위치를 정한 후 상단의 <strong>"확인" 버튼</strong>을 클릭하면 고정됩니다.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-amber-600 mb-2 flex items-center gap-2">
                    <i className="ri-delete-bin-line" />
                    삭제하기
                  </h3>
                  <p className="text-sm leading-relaxed">
                    등록 전 메모나 사진 위에 마우스를 올리면 <strong>빨간색 X 버튼</strong>이 나타납니다. 
                    이 버튼을 클릭하면 삭제할 수 있습니다. 등록 후에는 관리자 페이지에서만 삭제 가능합니다.
                  </p>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <i className="ri-lightbulb-line" />
                    팁
                  </h3>
                  <ul className="text-sm space-y-1 text-amber-900">
                    <li>• 메모와 사진은 자동으로 약간 기울어져 자연스러운 느낌을 줍니다</li>
                    <li>• 여러 개의 메모와 사진을 자유롭게 배치할 수 있습니다</li>
                    <li>• 모든 내용은 실시간으로 저장되어 새로고침해도 유지됩니다</li>
                  </ul>
                </div>
              </div>

              <button
                className="w-full mt-6 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
                onClick={() => setShowHelpModal(false)}
              >
                확인
              </button>
            </div>
          </div>
        )}

        {placingNoteId && (
          <div className="fixed top-4 md:top-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-[9999] px-4">
            <div className="bg-amber-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-full shadow-2xl flex items-center gap-2 animate-bounce text-sm md:text-base">
              <i className="ri-drag-move-line text-lg md:text-xl" />
              <span className="font-medium">메모를 원하는 위치로 드래그하세요</span>
            </div>
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-6 md:px-8 py-2 md:py-3 rounded-full shadow-2xl font-semibold transition-all hover:scale-105 whitespace-nowrap text-sm md:text-base"
              onClick={confirmPlacement}
            >
              확인
            </button>
          </div>
        )}

        {allItems.map(item => {
          const isTempItem = tempItems.some(t => t.id === item.id);
          return (
            <div
              key={item.id}
              className={`absolute ${item.is_locked && placingNoteId !== item.id ? 'cursor-default' : 'cursor-move'}`}
              style={{
                left: `${item.position_x}px`,
                top: `${item.position_y}px`,
                zIndex: item.z_index,
                transform: `rotate(${item.rotation}deg)`
              }}
              onMouseDown={(e) => handleMouseDown(e, item.id)}
              onTouchStart={(e) => handleTouchStart(e, item.id)}
            >
              {item.type === 'note' ? (
                <div className="relative group">
                  <div 
                    className="w-48 h-48 shadow-lg p-4 relative"
                    style={{ 
                      backgroundColor: item.color || '#fef08a',
                      backgroundImage: item.pattern && item.pattern !== 'none' 
                        ? `url("${getPatternSVG(item.pattern, item.color || '#fef08a')}")` 
                        : 'none',
                      backgroundRepeat: 'repeat',
                      backgroundSize: '40px 40px'
                    }}
                  >
                    <div
                      className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-6 bg-black/5"
                      style={{
                        clipPath: 'polygon(10% 0%, 90% 0%, 95% 100%, 5% 100%)'
                      }}
                    />
                    <div
                      className="w-full h-full text-gray-800 text-base leading-relaxed overflow-hidden break-words whitespace-pre-wrap"
                      style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                    >
                      {item.content || ''}
                    </div>
                  </div>
                  {(isTempItem || (!item.is_locked && placingNoteId === item.id)) && (
                    <button
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative group">
                  <div className="bg-white p-3 shadow-xl w-[220px]">
                    <img
                      src={item.photo_url}
                      alt="Polaroid"
                      className="w-52 h-52 object-contain bg-gray-50"
                      draggable={false}
                    />
                    <div 
                      className="h-12 bg-white flex items-center justify-center px-2 w-52"
                    >
                      <span 
                        className="text-gray-700 text-sm text-center line-clamp-2 w-full break-words"
                        style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                      >
                        {item.caption || ''}
                      </span>
                    </div>
                  </div>
                  {(isTempItem || (!item.is_locked && placingNoteId === item.id)) && (
                    <button
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <button
          className="fixed bottom-8 right-8 w-12 h-12 md:w-14 md:h-14 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-2xl flex items-center justify-center text-xl md:text-2xl transition-all hover:scale-110 z-[9999] cursor-pointer"
          onClick={() => setShowAddMenu(!showAddMenu)}
        >
          +
        </button>

        {showAddMenu && (
          <div className="fixed bottom-20 md:bottom-24 right-8 bg-white rounded-lg shadow-2xl p-2 z-[9999]">
            <button
              className="w-full px-6 py-3 text-left hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3 whitespace-nowrap"
              onClick={addNote}
            >
              <i className="ri-sticky-note-line text-xl text-yellow-500" />
              <span className="text-sm font-medium">메모 추가</span>
            </button>
            <button
              className="w-full px-6 py-3 text-left hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3 whitespace-nowrap"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-image-line text-xl text-blue-500" />
              <span className="text-sm font-medium">사진 추가</span>
            </button>
            <button
              className="w-full px-6 py-3 text-left hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3 whitespace-nowrap"
              onClick={() => {
                setShowGroupCreate(true);
                setShowAddMenu(false);
              }}
            >
              <i className="ri-group-line text-xl text-green-500" />
              <span className="text-sm font-medium">그룹 게시판 생성</span>
            </button>
          </div>
        )}

        {/* 그룹 생성 모달 */}
        {showGroupCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw] md:max-w-[480px] max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">그룹 게시판 생성</h3>
              
              <input
                type="text"
                className="w-full p-3 border-2 border-gray-200 rounded-lg outline-none focus:border-amber-500 transition-colors text-gray-800"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value.toLowerCase())}
                placeholder="그룹 이름을 영문으로 입력하세요."
                autoFocus
              />
              
              <p className="text-sm text-gray-500 mt-2 mb-4">
                작성하신 이름은 "memory-board.com/" 뒤에 주소로 사용됩니다.
              </p>

              <div className="mb-4">
                <input
                  type="password"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg outline-none focus:border-amber-500 transition-colors text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={newGroupPassword}
                  onChange={(e) => setNewGroupPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요."
                  disabled={noPassword}
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    checked={noPassword}
                    onChange={(e) => {
                      setNoPassword(e.target.checked);
                      if (e.target.checked) {
                        setNewGroupPassword('');
                      }
                    }}
                  />
                  <span className="text-sm text-gray-700">비밀번호 없음</span>
                </label>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={handleCreateGroup}
                >
                  생성
                </button>
                <button
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={() => {
                    setShowGroupCreate(false);
                    setNewGroupName('');
                    setNewGroupPassword('');
                    setNoPassword(false);
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {editingNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw] md:max-w-[480px] max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">메모 작성</h3>
              
              {/* 색상 선택 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">메모지 색상</label>
                <div className="flex gap-2 flex-wrap">
                  {noteColors.map((colorOption) => (
                    <button
                      key={colorOption.color}
                      className={`w-12 h-12 rounded-lg transition-all hover:scale-110 ${
                        editingNote.color === colorOption.color 
                          ? 'ring-4 ring-amber-500 ring-offset-2' 
                          : 'hover:ring-2 hover:ring-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: colorOption.color,
                        border: `2px solid ${colorOption.border}`
                      }}
                      onClick={() => setEditingNote({ ...editingNote, color: colorOption.color })}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </div>

              {/* 무늬 선택 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">무늬 선택</label>
                <div className="flex gap-2 flex-wrap">
                  {notePatterns.map((patternOption) => (
                    <button
                      key={patternOption.value}
                      className={`w-12 h-12 rounded-lg transition-all hover:scale-110 flex items-center justify-center ${
                        editingNote.pattern === patternOption.value 
                          ? 'ring-4 ring-amber-500 ring-offset-2 bg-amber-50' 
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      onClick={() => setEditingNote({ ...editingNote, pattern: patternOption.value })}
                      title={patternOption.name}
                    >
                      <i className={`${patternOption.icon} text-xl text-gray-700`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* 미리보기 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">미리보기</label>
                <div 
                  className="w-32 h-32 rounded-lg shadow-md p-3 mx-auto"
                  style={{ 
                    backgroundColor: editingNote.color,
                    backgroundImage: editingNote.pattern && editingNote.pattern !== 'none' 
                      ? `url("${getPatternSVG(editingNote.pattern, editingNote.color)}")` 
                      : 'none',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '40px 40px'
                  }}
                >
                  <div
                    className="text-gray-800 text-xs leading-relaxed overflow-hidden"
                    style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                  >
                    {editingNote.content || '메모 내용...'}
                  </div>
                </div>
              </div>

              <textarea
                className="w-full h-48 p-3 border-2 border-gray-200 rounded-lg resize-none outline-none focus:border-amber-500 transition-colors text-gray-800"
                value={editingNote.content}
                onChange={(e) => {
                  const text = e.target.value;
                  const lines = text.split('\n');
                  
                  // 6줄 제한
                  if (lines.length <= 6) {
                    setEditingNote({ ...editingNote, content: text });
                  } else {
                    // 6줄까지만 허용
                    const limitedText = lines.slice(0, 6).join('\n');
                    setEditingNote({ ...editingNote, content: limitedText });
                  }
                }}
                placeholder="메모 내용을 입력하세요..."
                autoFocus
                maxLength={170}
                style={{ fontFamily: "'Nanum Pen Script', cursive" }}
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {editingNote.content.length} / 170자 (최대 6줄)
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={handleNoteSave}
                >
                  확인
                </button>
                <button
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={handleNoteCancel}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {uploadingPhoto && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw] md:max-w-96">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">사진 설명 입력</h3>
              <div className="mb-4 flex justify-center">
                <img 
                  src={uploadingPhoto.url} 
                  alt="Preview" 
                  className="w-48 h-48 object-contain bg-gray-50 rounded-lg"
                />
              </div>
              <textarea
                className="w-full h-24 p-3 border-2 border-gray-200 rounded-lg resize-none outline-none focus:border-amber-500 transition-colors text-gray-800"
                value={uploadingPhoto.caption}
                onChange={(e) => setUploadingPhoto({ ...uploadingPhoto, caption: e.target.value })}
                placeholder="사진 설명을 입력하세요..."
                autoFocus
                maxLength={40}
                style={{ fontFamily: "'Nanum Pen Script', cursive" }}
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {uploadingPhoto.caption.length} / 40자
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={handlePhotoSave}
                >
                  확인
                </button>
                <button
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                  onClick={handlePhotoCancel}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handlePhotoUpload}
        />
      </div>
    );
  }

  // 모바일 버전 렌더링
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* 헤더 */}
      <div className="sticky top-0 z-[9999] bg-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-amber-700 flex items-center gap-2">
            <i className="ri-pushpin-line" />
            코르크보드
          </h1>
          <button
            className="w-10 h-10 bg-amber-600 text-white rounded-full shadow-lg flex items-center justify-center text-xl cursor-pointer"
            onClick={() => setShowAddMenu(!showAddMenu)}
          >
            +
          </button>
        </div>
      </div>

      {/* 메모/사진 리스트 */}
      <div className="p-4 space-y-4 pb-20 touch-pan-y">
        {items.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-sticky-note-line text-6xl text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">메모나 사진을 추가해보세요</p>
          </div>
        ) : (
          items.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              {item.type === 'note' ? (
                <div 
                  className="p-6 relative"
                  style={{ 
                    backgroundColor: item.color || '#fef08a',
                    backgroundImage: item.pattern && item.pattern !== 'none' 
                      ? `url("${getPatternSVG(item.pattern, item.color || '#fef08a')}")` 
                      : 'none',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '40px 40px'
                  }}
                >
                  <div
                    className="text-gray-800 text-lg leading-relaxed whitespace-pre-wrap break-words min-h-[100px]"
                    style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                  >
                    {item.content || ''}
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={item.photo_url}
                    alt="Photo"
                    className="w-full h-64 object-cover"
                  />
                  {item.caption && (
                    <div 
                      className="p-4 bg-white text-center"
                      style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                    >
                      <p className="text-gray-700 text-base">{item.caption}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 추가 메뉴 */}
      {showAddMenu && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-end" onClick={() => setShowAddMenu(false)}>
          <div className="bg-white w-full rounded-t-3xl p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <button
              className="w-full px-6 py-4 bg-yellow-100 hover:bg-yellow-200 rounded-xl transition-colors flex items-center gap-4 text-left"
              onClick={addNote}
            >
              <i className="ri-sticky-note-line text-2xl text-yellow-600" />
              <span className="text-lg font-medium text-gray-800">메모 추가</span>
            </button>
            <button
              className="w-full px-6 py-4 bg-blue-100 hover:bg-blue-200 rounded-xl transition-colors flex items-center gap-4 text-left"
              onClick={() => {
                fileInputRef.current?.click();
                setShowAddMenu(false);
              }}
            >
              <i className="ri-image-line text-2xl text-blue-600" />
              <span className="text-lg font-medium text-gray-800">사진 추가</span>
            </button>
            <button
              className="w-full px-6 py-4 bg-green-100 hover:bg-green-200 rounded-xl transition-colors flex items-center gap-4 text-left"
              onClick={() => {
                setShowGroupCreate(true);
                setShowAddMenu(false);
              }}
            >
              <i className="ri-group-line text-2xl text-green-600" />
              <span className="text-lg font-medium text-gray-800">그룹 게시판 생성</span>
            </button>
          </div>
        </div>
      )}

      {/* 메모 작성 모달 */}
      {editingNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw] max-h-[90vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">메모 작성</h3>
            
            {/* 색상 선택 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">메모지 색상</label>
              <div className="flex gap-2 flex-wrap">
                {noteColors.map((colorOption) => (
                  <button
                    key={colorOption.color}
                    className={`w-12 h-12 rounded-lg transition-all ${
                      editingNote.color === colorOption.color 
                        ? 'ring-4 ring-amber-500 ring-offset-2' 
                        : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                    style={{ 
                      backgroundColor: colorOption.color,
                      border: `2px solid ${colorOption.border}`
                    }}
                    onClick={() => setEditingNote({ ...editingNote, color: colorOption.color })}
                    title={colorOption.name}
                  />
                ))}
              </div>
            </div>

            {/* 무늬 선택 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">무늬 선택</label>
              <div className="flex gap-2 flex-wrap">
                {notePatterns.map((patternOption) => (
                  <button
                    key={patternOption.value}
                    className={`w-12 h-12 rounded-lg transition-all flex items-center justify-center ${
                      editingNote.pattern === patternOption.value 
                        ? 'ring-4 ring-amber-500 ring-offset-2 bg-amber-50' 
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                    onClick={() => setEditingNote({ ...editingNote, pattern: patternOption.value })}
                    title={patternOption.name}
                  >
                    <i className={`${patternOption.icon} text-xl text-gray-700`} />
                  </button>
                ))}
              </div>
            </div>

            {/* 미리보기 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">미리보기</label>
              <div 
                className="w-32 h-32 rounded-lg shadow-md p-3 mx-auto"
                style={{ 
                  backgroundColor: editingNote.color,
                  backgroundImage: editingNote.pattern && editingNote.pattern !== 'none' 
                    ? `url("${getPatternSVG(editingNote.pattern, editingNote.color)}")` 
                    : 'none',
                  backgroundRepeat: 'repeat',
                  backgroundSize: '40px 40px'
                }}
              >
                <div
                  className="text-gray-800 text-xs leading-relaxed overflow-hidden"
                  style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                >
                  {editingNote.content || '메모 내용...'}
                </div>
              </div>
            </div>

            <textarea
              className="w-full h-48 p-3 border-2 border-gray-200 rounded-lg resize-none outline-none focus:border-amber-500 transition-colors text-gray-800"
              value={editingNote.content}
              onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
              placeholder="메모 내용을 입력하세요..."
              autoFocus
              maxLength={170}
              style={{ fontFamily: "'Nanum Pen Script', cursive" }}
            />
            <div className="text-right text-sm text-gray-500 mt-1">
              {editingNote.content.length} / 170자
            </div>
            <div className="flex gap-3 mt-4">
              <button
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={handleNoteSave}
              >
                확인
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={handleNoteCancel}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 그룹 생성 모달 */}
      {showGroupCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">그룹 게시판 생성</h3>
            
            <input
              type="text"
              className="w-full p-3 border-2 border-gray-200 rounded-lg outline-none focus:border-amber-500 transition-colors text-gray-800"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value.toLowerCase())}
              placeholder="그룹 이름을 영문으로 입력하세요."
              autoFocus
            />
            
            <p className="text-sm text-gray-500 mt-2 mb-4">
              작성하신 이름은 "memory-board.com/" 뒤에 주소로 사용됩니다.
            </p>

            <div className="mb-4">
              <input
                type="password"
                className="w-full p-3 border-2 border-gray-200 rounded-lg outline-none focus:border-amber-500 transition-colors text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={newGroupPassword}
                onChange={(e) => setNewGroupPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요."
                disabled={noPassword}
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  checked={noPassword}
                  onChange={(e) => {
                    setNoPassword(e.target.checked);
                    if (e.target.checked) {
                      setNewGroupPassword('');
                    }
                  }}
                />
                <span className="text-sm text-gray-700">비밀번호 없음</span>
              </label>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={handleCreateGroup}
              >
                생성
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={() => {
                  setShowGroupCreate(false);
                  setNewGroupName('');
                  setNewGroupPassword('');
                  setNoPassword(false);
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사진 설명 입력 모달 */}
      {uploadingPhoto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-[90vw]">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">사진 설명 입력</h3>
            <div className="mb-4 flex justify-center">
              <img 
                src={uploadingPhoto.url} 
                alt="Preview" 
                className="w-48 h-48 object-contain bg-gray-50 rounded-lg"
              />
            </div>
            <textarea
              className="w-full h-24 p-3 border-2 border-gray-200 rounded-lg resize-none outline-none focus:border-amber-500 transition-colors text-gray-800"
              value={uploadingPhoto.caption}
              onChange={(e) => setUploadingPhoto({ ...uploadingPhoto, caption: e.target.value })}
              placeholder="사진 설명을 입력하세요..."
              autoFocus
              maxLength={40}
              style={{ fontFamily: "'Nanum Pen Script', cursive" }}
            />
            <div className="text-right text-sm text-gray-500 mt-1">
              {uploadingPhoto.caption.length} / 40자
            </div>
            <div className="flex gap-3 mt-4">
              <button
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={handlePhotoSave}
              >
                확인
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                onClick={handlePhotoCancel}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoUpload}
      />
    </div>
  );
}
